import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Siren } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import {
  INCIDENT_CATEGORIES, INCIDENT_CATEGORY_LABELS, INCIDENT_STATUS_COLORS, INCIDENT_STATUS_LABELS,
  SEVERITY_COLORS,
} from "@/config/incidentCatalog";

export default function IncidentsPage() {
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({ category: "", status: "", date_from: "", date_to: "" });

  const load = (f = filters) => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/incidents", { params }).then((r) => setItems(r.data.items)).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const apply = (key, value) => {
    const next = { ...filters, [key]: value === "all" ? "" : value };
    setFilters(next);
    load(next);
  };

  return (
    <div className="space-y-4" data-testid="incidents-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Incidents</h1>
        <Link to="/incidents/new" data-testid="incident-new-button"
          className="inline-flex h-11 items-center gap-2 rounded-md bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 transition-colors duration-200">
          <Plus className="h-4 w-4" /> Report incident
        </Link>
      </div>

      <Card>
        <CardContent className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Select value={filters.category || "all"} onValueChange={(v) => apply("category", v)}>
            <SelectTrigger data-testid="incidents-filter-category" className="h-11"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All categories</SelectItem>
              {INCIDENT_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status || "all"} onValueChange={(v) => apply("status", v)}>
            <SelectTrigger data-testid="incidents-filter-status" className="h-11"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(INCIDENT_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="incidents-filter-from" type="date" value={filters.date_from} onChange={(e) => apply("date_from", e.target.value)} />
          <Input data-testid="incidents-filter-to" type="date" value={filters.date_to} onChange={(e) => apply("date_to", e.target.value)} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Siren className="h-5 w-5 text-red-600" /> Incident records ({items?.length ?? "…"})</CardTitle></CardHeader>
        <CardContent>
          {items === null ? (
            <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-600" data-testid="incidents-empty">No incidents match the selected filters.</p>
          ) : (
            <div className="space-y-2">
              {items.map((i) => (
                <Link key={i.id} to={`/incidents/${i.id}`} data-testid={`incident-row-${i.incident_number}`}
                  className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3 hover:bg-muted transition-colors duration-200">
                  <div className="min-w-[150px]">
                    <p className="font-mono font-semibold text-slate-900">{i.incident_number}</p>
                    <p className="text-xs text-slate-500">{i.child?.child_name} · {i.child?.case_id}</p>
                  </div>
                  <div className="text-xs text-slate-600 min-w-[160px]">
                    <p>{INCIDENT_CATEGORY_LABELS[i.category] || i.category || "Uncategorised"}</p>
                    <p>{i.incident_date} · reported by {i.reporter_name}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 ml-auto">
                    {i.severity === "critical" && <Badge className={SEVERITY_COLORS.critical} data-testid={`incident-critical-${i.id}`}>CRITICAL</Badge>}
                    {i.is_near_miss && <Badge className="bg-indigo-50 text-indigo-700 border-transparent">Near miss</Badge>}
                    {!i.detail_complete && <Badge className="bg-amber-100 text-amber-800 border-transparent" data-testid={`incident-followup-${i.id}`}>Follow-up information required</Badge>}
                    {i.open_actions > 0 && <Badge className="bg-amber-100 text-amber-800 border-transparent">{i.open_actions} open action{i.open_actions > 1 ? "s" : ""}</Badge>}
                    {i.safeguarding_escalation && <Badge className="bg-red-50 text-red-700 border-transparent">Safeguarding linked</Badge>}
                    <Badge className={INCIDENT_STATUS_COLORS[i.status]}>{INCIDENT_STATUS_LABELS[i.status]}</Badge>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
